/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { Images } from "@packages/sdk";
import type { JSX, ReactNode } from "react";
import { forwardRef, useState } from "react";
import { useFocus, useHover, usePress } from "react-aria";

import type { ThemeType } from "../../../../../../global/emotion";
import { radius, spacing, stroke } from "../../../../../../global/emotion";
import { stylesOpacity } from "../../../../../../global/emotion/styles/stylesOpacity";
import { stylesOutline } from "../../../../../../global/emotion/styles/stylesOutline";
import { stylesScale } from "../../../../../../global/emotion/styles/stylesScale";
import type { HallowElement } from "../../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../../types/withAsChild.types";
import type { CSSColorStrict } from "../../../../../utils/colors/types/CSSColorStrict.types";
import { determineChildrenInject } from "../../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../../utils/determineElementFromAsChild";
import { HallowImage } from "../../../../blocks/other/HallowImage/HallowImage";
import { Text } from "../../../_core/Text/Text.emotion";

const hallowElement: HallowElement = "div";

const determinedElementStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  minWidth: spacing.none,
};
const illustrationImgStyle: Interpolation = {
  WebkitUserDrag: "none",
  borderBottomLeftRadius: spacing.s,
  borderTopRightRadius: spacing.s,
  bottom: spacing.none,
  height: spacing.xxl,
  left: spacing.none,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  position: "absolute",
  userSelect: "none",
  width: spacing.xxl,
};
const imgStyle: Interpolation<CSSColorStrict> = (color) => ({
  WebkitUserDrag: "none",
  borderColor: color,
  borderRadius: radius.s,
  borderStyle: "solid",
  borderWidth: stroke.heavy,
  height: spacing.full,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  position: "absolute",
  top: 0,
  userSelect: "none",
  width: spacing.full,
});
const imgContainerStyle: Interpolation = {
  WebkitUserDrag: "none",
  paddingBottom: "70%",
  position: "relative",
  userSelect: "none",
  width: spacing.full,
};
const infoContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  minWidth: spacing.none,
  paddingLeft: spacing.xxs,
  paddingRight: spacing.xxs,
};
const infoTopContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.t,
};
const metadataStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
});

export type CampaignMediumProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  activeState?: boolean;
  color: CSSColorStrict;
  description: string;
  focusState?: boolean;
  hoverState?: boolean;
  illustrationImgSrc: string;
  imgSrc: Images;
  metadata: ReactNode;
  title: string;
  imgContainerProps?: HallowElementProps<"div">;
};

export const CampaignMedium = forwardRef<any, CampaignMediumProps>(
  (
    {
      activeState = true,
      asChild = false,
      children,
      color,
      description,
      focusState = true,
      hoverState = true,
      illustrationImgSrc,
      imgSrc,
      metadata,
      title,
      imgContainerProps = {},
      ...props
    },
    ref,
  ) => {
    const [focus, setFocus] = useState(false);

    const { pressProps: activeProps } = usePress({});
    const { focusProps } = useFocus({
      onFocusChange: (focus) => setFocus(focus),
    });
    const { isHovered: hover, hoverProps } = useHover({});

    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const determinedChildren = determineChildrenInject({
      afterChildrenInject: [
        <div key="imgContainer" css={imgContainerStyle} {...imgContainerProps}>
          <HallowImage
            alt={title}
            src={imgSrc}
            size={"m"}
            css={[
              imgStyle(color),
              stylesOutline.base,
              focus && focusState ? stylesOutline.focusControlled : null,
              hover && hoverState ? stylesOutline.hoverControlled : null,
            ]}
          />
          <img
            alt={title}
            src={illustrationImgSrc}
            css={illustrationImgStyle}
          />
          {(imgContainerProps as HallowElementProps<"div">).children}
        </div>,
        <div key="infoContainer" css={infoContainerStyle}>
          <div css={infoTopContainerStyle}>
            <Text overflow="ellipsis" size="l" type="title">
              {title}
            </Text>
            <Text overflow="ellipsis" size="s" type="body">
              {description}
            </Text>
          </div>
          <Text overflow="ellipsis" size="l" type="detail" css={metadataStyle}>
            {metadata}
          </Text>
        </div>,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        ref={ref}
        {...activeProps}
        {...focusProps}
        {...hoverProps}
        css={[
          determinedElementStyle,
          activeState ? stylesOpacity.active : null,
          activeState ? stylesOpacity.base : null,
          activeState && hoverState ? stylesScale.base : null,
          activeState && hoverState ? stylesScale.m : null,
        ]}
        {...props}
      >
        {determinedChildren}
      </DeterminedElement>
    );
  },
);

CampaignMedium.displayName = "CampaignMedium";
